import { freshPage } from "../session";
import { useCallback, useEffect, useId, useState, type FormEvent } from "react";
import { portalApi } from "../api";
import { codeOf, errorText, statusOf } from "../errors";
import { dateTime } from "../format";
import { isLang, useI18n } from "../i18n";
import { usePortal } from "../state";
import type { Device, Lang, OrgPlanView } from "../types";
import { PlanSummary, UpgradeSection } from "./Plan";
import { Field, Link, Notice, PageTitle } from "../ui";

/**
 * The signed-in account: for an organisation's purchaser its subscription
 * and the upsell first, then profile, password change, devices and sign out.
 *
 * Sigge 2026-10-07 removed email change, the data download and account
 * deletion from this page: an organisation account's contact and lifecycle
 * belong to the provider and Nenda, not to a self-service button. (The API
 * routes remain; a data request is handled by Nenda support.)
 */

type Msg = { kind: "success" | "error"; text: string } | null;

function useSignedOutOn401() {
  const { setMe } = usePortal();
  return useCallback(
    (err: unknown): boolean => {
      if (statusOf(err) === 401 && codeOf(err) !== "invalid_credentials") {
        setMe(null);
        return true;
      }
      return false;
    },
    [setMe]
  );
}

export function AccountScreen() {
  const { t } = useI18n();
  const { me } = usePortal();
  if (!me) {
    return (
      <section className="panel panel--narrow">
        <PageTitle>{t("account.title")}</PageTitle>
        <p>{t("account.loginFirst")}</p>
        <Link className="btn" to="/login?next=%2Faccount">
          {t("nav.login")}
        </Link>
      </section>
    );
  }
  return (
    <>
      <PageTitle>{t("account.title")}</PageTitle>
      <p className="muted">{me.email}</p>
      {!me.emailVerifiedAt && <Notice>{t("account.unverified")}</Notice>}
      {me.kind === "purchaser" && <SubscriptionSection />}
      <ProfileSection />
      <PasswordSection />
      <DevicesSection />
      <SignOutSection />
    </>
  );
}

function ProfileSection() {
  const { t } = useI18n();
  const { me, setMe } = usePortal();
  const [name, setName] = useState(me?.displayName ?? "");
  const [locale, setLocale] = useState<Lang>(
    me?.locale && isLang(me.locale) ? me.locale : "sv"
  );
  const [marketing, setMarketing] = useState(!!me?.marketingOptIn);
  const [msg, setMsg] = useState<Msg>(null);
  const [busy, setBusy] = useState(false);
  const signedOut = useSignedOutOn401();
  const localeId = useId();
  const marketingId = useId();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setMsg(null);
    setBusy(true);
    try {
      const updated = await portalApi.updateProfile({
        displayName: name.trim(),
        locale,
        marketingOptIn: marketing,
      });
      const { emailChangePending: _pending, ...profile } = updated;
      setMe(profile);
      setMsg({ kind: "success", text: t("common.saved") });
    } catch (err) {
      if (!signedOut(err)) setMsg({ kind: "error", text: errorText(err, t) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel" aria-labelledby="profile-h">
      <h2 id="profile-h">{t("account.profile")}</h2>
      <form onSubmit={(e) => void submit(e)}>
        <Field
          label={t("account.name")}
          autoComplete="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <div className="field">
          <label htmlFor={localeId}>{t("account.locale")}</label>
          <select
            id={localeId}
            value={locale}
            onChange={(e) => setLocale(e.target.value as Lang)}
          >
            <option value="sv">{t("lang.sv")}</option>
            <option value="en">{t("lang.en")}</option>
          </select>
        </div>
        <div className="consent">
          <input
            id={marketingId}
            type="checkbox"
            checked={marketing}
            onChange={(e) => setMarketing(e.target.checked)}
          />
          <label htmlFor={marketingId}>{t("account.marketing")}</label>
        </div>
        {msg && <Notice kind={msg.kind}>{msg.text}</Notice>}
        <button type="submit" className="btn" disabled={busy}>
          {busy ? t("common.saving") : t("common.save")}
        </button>
      </form>
    </section>
  );
}

function PasswordSection() {
  const { t } = useI18n();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [msg, setMsg] = useState<Msg>(null);
  const [busy, setBusy] = useState(false);
  const signedOut = useSignedOutOn401();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setMsg(null);
    if (Array.from(next).length < 10) {
      setMsg({ kind: "error", text: t("auth.tooShort") });
      return;
    }
    setBusy(true);
    try {
      await portalApi.changePassword(current, next);
      setMsg({ kind: "success", text: t("account.passwordChanged") });
      setCurrent("");
      setNext("");
    } catch (err) {
      if (signedOut(err)) return;
      setMsg({
        kind: "error",
        text: errorText(err, t, {
          invalid_credentials: "account.wrongPassword",
        }),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel" aria-labelledby="password-h">
      <h2 id="password-h">{t("account.passwordTitle")}</h2>
      <form onSubmit={(e) => void submit(e)}>
        <Field
          label={t("account.currentPassword")}
          type="password"
          autoComplete="current-password"
          required
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
        />
        <Field
          label={t("auth.newPassword")}
          hint={t("common.passwordHint")}
          type="password"
          autoComplete="new-password"
          required
          minLength={10}
          value={next}
          onChange={(e) => setNext(e.target.value)}
        />
        {msg && <Notice kind={msg.kind}>{msg.text}</Notice>}
        <button type="submit" className="btn" disabled={busy}>
          {t("account.passwordChange")}
        </button>
      </form>
    </section>
  );
}

function DevicesSection() {
  const { t, lang } = useI18n();
  const { setMe, go } = usePortal();
  const [devices, setDevices] = useState<Device[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const signedOut = useSignedOutOn401();

  const load = useCallback(async () => {
    try {
      setDevices((await portalApi.devices()).devices);
    } catch (err) {
      if (!signedOut(err)) setError(errorText(err, t));
    }
  }, [signedOut, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const signOut = async (d: Device) => {
    setError(null);
    try {
      await portalApi.signOutDevice(d.id);
      if (d.current) {
        setMe(null);
        go("/");
        return;
      }
      await load();
    } catch (err) {
      if (!signedOut(err)) setError(errorText(err, t));
    }
  };

  return (
    <section className="panel" aria-labelledby="devices-h">
      <h2 id="devices-h">{t("account.devices")}</h2>
      {error && <Notice kind="error">{error}</Notice>}
      {devices && devices.length === 0 && <p>{t("account.device.none")}</p>}
      {devices && devices.length > 0 && (
        <ul className="devices">
          {devices.map((d) => {
            const label =
              d.deviceLabel ||
              (d.kind === "app"
                ? t("account.device.app")
                : t("account.device.web"));
            return (
              <li key={d.id} className="device">
                <div>
                  <strong>{label}</strong>
                  {d.current && (
                    <span className="badge badge--ok">
                      {t("account.device.current")}
                    </span>
                  )}
                  <p className="muted">
                    {d.kind === "app"
                      ? t("account.device.app")
                      : t("account.device.web")}{" "}
                    ·{" "}
                    {t("account.device.lastUsed", {
                      date: dateTime(d.lastUsedAt, lang),
                    })}
                  </p>
                </div>
                <button
                  type="button"
                  className="btn btn--quiet"
                  onClick={() => void signOut(d)}
                  aria-label={`${t("account.device.signOut")}: ${label}`}
                >
                  {t("account.device.signOut")}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function SignOutSection() {
  const { t } = useI18n();
  const [error, setError] = useState<string | null>(null);

  const logout = async () => {
    await portalApi.logout().catch(() => undefined);
    freshPage("/");
  };
  const everywhere = async () => {
    setError(null);
    try {
      await portalApi.logoutEverywhere();
      freshPage("/");
    } catch (err) {
      if (statusOf(err) === 401) {
        freshPage("/");
        return;
      }
      setError(errorText(err, t));
    }
  };

  return (
    <section className="panel" aria-labelledby="signout-h">
      <h2 id="signout-h">{t("account.logout")}</h2>
      <p className="row">
        <button type="button" className="btn" onClick={() => void logout()}>
          {t("account.logout")}
        </button>
        <button
          type="button"
          className="btn btn--quiet"
          onClick={() => void everywhere()}
        >
          {t("account.logoutAll")}
        </button>
      </p>
      <p className="muted">{t("account.logoutAllBody")}</p>
      {error && <Notice kind="error">{error}</Notice>}
    </section>
  );
}

/**
 * "Ert abonnemang" on Konto (purchaser only): the package the organisation
 * has, and every higher one with what it costs now - the upsell. Picking
 * films and sharing viewer accounts stay on the subscription page.
 */
function SubscriptionSection() {
  const { t } = useI18n();
  const [view, setView] = useState<OrgPlanView | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    portalApi
      .org()
      .then((v) => live && setView(v))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, []);
  if (failed) return null;
  if (!view) return null;
  return (
    <div className="account-subscription">
      <h2 className="section-title">{t("plan.title")}</h2>
      {notice && <Notice kind="success">{notice}</Notice>}
      <PlanSummary view={view} />
      <p>
        <Link to="/plan">{t("account.manageSubscription")}</Link>
      </p>
      <UpgradeSection
        view={view}
        onUpgraded={(next, label) => {
          setView(next);
          setNotice(t("plan.upgraded", { plan: label }));
        }}
      />
    </div>
  );
}
